import {
  CloudDownloadOutlined,
  DeleteOutlined,
  FilePdfOutlined,
  HomeOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import {
  Breadcrumb,
  Button,
  Col,
  Empty,
  Form,
  Input,
  Pagination,
  Popconfirm,
  Row,
  Space,
  Table,
  Upload,
} from "antd";
import React, { useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { openNotification } from "../../../components/Notification";
import { UserContext } from "../../../contexts/UserContext";

import {
  isEmpty,
  OPCOES_TAMANHO_PAGINA,
  parsedSort,
  removeEmpty,
  USER_ROLES,
} from "../../../utils";
import { pdfService } from "../../../services/resources/pdfService";
import { useWidth } from "../../../hooks";

const MAX_SIZE_FILE_UPLOAD = 30;
const CONVERT_SIZE_FILE_MB = 1024 * 1024;

export const Anexos = () => {
  const { user } = useContext(UserContext);
  const [form] = Form.useForm();

  const [anexos, setAnexos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const { width } = useWidth();
  const [filter, setFilter] = useState({
    ativo: true,
    page: 0,
    size: OPCOES_TAMANHO_PAGINA[0],
  });
  const [paginacao, setPaginacao] = useState({
    currentPage: 0,
    totalElements: 0,
    size: OPCOES_TAMANHO_PAGINA[0],
  });

  const fetchAnexos = async (filter) => {
    setLoading(true);
    try {
      const response = await pdfService.list(removeEmpty(filter));
      setAnexos(response?.data?.content);
      setPaginacao({
        currentPage: response?.data?.currentPage,
        totalElements: response?.data?.totalElements,
        size: response?.data?.size,
      });
    } catch (error) {
      openNotification(
        "error",
        <strong>Erro ao buscar anexos!</strong>,
        error.response?.data?.detail
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnexos(filter);
  }, [filter]);

  function onChange(_, filters, sorter, { action }) {
    if (action === "sort") onSort(sorter);
    if (action === "filter") onFilter(filters);
  }

  function onSort(sorter) {
    if (sorter.constructor === Object) {
      if (sorter.column)
        setFilter({
          ...filter,
          sort: parsedSort(sorter.columnKey, sorter.order),
        });
      else setFilter({ ...filter, sort: [] });
    } else {
      setFilter({
        ...filter,
        sort: sorter.map((sort) => parsedSort(sort.columnKey, sort.order)),
      });
    }
  }

  function onFilter(filters) {
    setFilter({
      ...filter,
      ...filters,
      page: 0,
    });
  }

  const handleSearchChange = (numeroProtocolo) => {
    const newFilter = {
      ...filter,
      page: 0,
    };

    if (numeroProtocolo) {
      newFilter.numeroProtocolo = numeroProtocolo;
    } else {
      delete newFilter.numeroProtocolo;
    }

    setFilter(newFilter);
  };

  const filterInitial = {
    ativo: true,
    page: 0,
    size: OPCOES_TAMANHO_PAGINA[0],
  };

  const clearAll = () => {
    if (!isEmpty(removeEmpty(form.getFieldsValue()))) {
      setFilter(filterInitial);
      form.resetFields();
    }
  };

  const handleDelete = async (codigo) => {
    try {
      await pdfService.delete(codigo);
      openNotification("success", <strong>PDF removido com sucesso!</strong>);
      fetchAnexos(filter);
    } catch (error) {
      openNotification(
        "error",
        <strong>Erro ao remover PDF!</strong>,
        error.response?.data?.detail
      );
    }
  };

  async function handleDownloadAnexo(anexo) {
    setDownloadLoading(true);
    await pdfService
      .download(anexo.codigoArquivo)
      .then((response) => {
        var data = new Blob([response.data], { type: anexo.contentType });
        var csvURL = window.URL.createObjectURL(data);
        var tempLink = document.createElement("a");
        tempLink.href = csvURL;
        tempLink.setAttribute("download", anexo.nomeArquivo);
        tempLink.click();
      })
      .then(() => {
        openNotification(
          "success",
          <strong>Anexo baixado com sucesso!</strong>
        );
      })

      .catch((reason) => {
        openNotification(
          "error",
          <strong>Ocorreu um erro ao baixar o anexo!</strong>,
          reason?.response?.data?.detail
        );
      });
    setDownloadLoading(false);
  }

  const handleCustomUpload = async (options) => {
    const { onSuccess, onError, file } = options;

    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await pdfService.upload(formData);
      onSuccess(response.data, file);
    } catch (err) {
      onError(err);
    }
  };

  const uploadProps = {
    name: "file",
    customRequest: handleCustomUpload,
    showUploadList: false,
    beforeUpload: (file) => {
      const isPdf = file.type === "application/pdf";
      if (!isPdf) {
        openNotification(
          "error",
          <strong>{file.name} não é um arquivo PDF.</strong>
        );
        return Upload.LIST_IGNORE;
      }
      const isLt30M = file.size / CONVERT_SIZE_FILE_MB < MAX_SIZE_FILE_UPLOAD;
      if (!isLt30M) {
        openNotification(
          "error",
          <strong>
            O arquivo excede o limite de {MAX_SIZE_FILE_UPLOAD}MB.
          </strong>
        );
        return Upload.LIST_IGNORE;
      }
      return isPdf && isLt30M;
    },

    onChange(info) {
      if (info.file.status === "uploading") {
        setUploading(true);
        return;
      }
      if (info.file.status === "done") {
        setUploading(false);
        openNotification(
          "success",
          <strong>Arquivo {info.file.name} enviado com sucesso!</strong>
        );
        fetchAnexos(filter);
      } else if (info.file.status === "error") {
        setUploading(false);
        const errorMessage =
          info.file.error?.response?.data?.detail ||
          info.file.error?.response?.data?.message ||
          info.file.error?.response?.data?.userMessage ||
          "Tente novamente.";
        openNotification(
          "error",
          <strong>Erro no upload do arquivo {info.file.name}.</strong>,
          errorMessage
        );
      }
    },
  };

  const columns = [
    {
      key: "icon",
      width: 50,
      render: () => <FilePdfOutlined style={{ fontSize: "1.5rem" }} />,
    },

    {
      title: <strong>Nome do Arquivo</strong>,
      dataIndex: "nomeArquivo",
      key: "nomeArquivo",
      render: (text) => text || "Não informado",
    },
    {
      title: <strong>Data de inserção</strong>,
      dataIndex: "dataOcorrencia",
      key: "dataCriacao",
      render: (text) => new Date(text).toLocaleDateString("pt-BR"),
    },
    {
      title: <strong>N° do protocolo</strong>,
      dataIndex: "numeroProtocolo",
      key: "numeroProtocolo",
      render: (text) => text || "Não informado",
    },
    {
      title: <strong>Data do fato</strong>,
      dataIndex: "dataCadastro",
      key: "dataCadastro",
      render: (text) =>
        text ? new Date(text).toLocaleDateString("pt-BR") : "",
    },
    {
      title: <strong>Ações</strong>,
      key: "action",
      width: 120,
      render: (anexo) => (
        <Space size="small">
          <Button
            type="dashed"
            icon={<CloudDownloadOutlined />}
            title={`Baixar o arquivo ${anexo.nomeArquivo}`}
            onClick={() => handleDownloadAnexo(anexo)}
            loading={downloadLoading}
          />
          {user?.authorities?.includes(USER_ROLES.DELETAR_DENUNCIA) && (
            <Popconfirm
              title="Tem certeza que quer excluir?"
              onConfirm={() => handleDelete(anexo.codigo)}
            >
              <Button type="danger" icon={<DeleteOutlined />} />
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div id="sectionMain">
      <Breadcrumb id="breadcrumb">
        <Breadcrumb.Item>
          <Link to="/">
            <HomeOutlined />
          </Link>
        </Breadcrumb.Item>
        <Breadcrumb.Item>
          <strong>Gerenciamento de anexos</strong>
        </Breadcrumb.Item>
      </Breadcrumb>
      <div className="container-section">
        <div id="header-container">
          <h2>GERENCIAMENTO DE ANEXOS</h2>
          <div id="header-actions">
            <Upload {...uploadProps}>
              <Button loading={uploading} type="primary">
                Adicionar Anexo
              </Button>
            </Upload>
          </div>
        </div>
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={24} md={8} lg={6}>
            <Form
              name="anexos_filtro"
              layout="inline"
              form={form}
              onValuesChange={({ numeroProtocolo }) =>
                handleSearchChange(numeroProtocolo)
              }
            >
              <Form.Item name="numeroProtocolo" style={{ width: "100%" }}>
                <Input
                  prefix={<SearchOutlined />}
                  placeholder="Buscar pelo número do protocolo"
                  allowClear
                />
              </Form.Item>
            </Form>
          </Col>
          <Col>
            <Button onClick={clearAll}>limpar filtros</Button>
          </Col>
        </Row>
        <Row>
          <Col span={24}>
            <Table
              columns={columns}
              dataSource={anexos}
              rowKey="codigo"
              loading={loading}
              pagination={false}
              onChange={onChange}
              size="small"
              locale={{
                emptyText: (
                  <Empty description={<h2>Sem anexos para essa denúncia</h2>} />
                ),
              }}
            />
          </Col>
        </Row>
        <Row justify="end">
          <Col>
            <Pagination
              size="small"
              showSizeChanger={true}
              simple={width < 768}
              pageSizeOptions={OPCOES_TAMANHO_PAGINA}
              defaultPageSize={paginacao.size}
              total={paginacao.totalElements}
              showTotal={(total) => <strong>{total} registros no total</strong>}
              onChange={(page, size) => {
                setFilter({
                  ...filter,
                  page: filter.size !== size ? 0 : page - 1,
                  size,
                });
              }}
              current={paginacao.currentPage + 1}
            />
          </Col>
        </Row>
      </div>
    </div>
  );
};
